import { z } from 'zod';
import {
  MandantTypeSchema as ContractMandantTypeSchema,
  CaseStatusSchema,
  CaseScenarioSchema,
  PvVariantSchema as SharedPvVariantSchema,
  type PvVariant as SharedPvVariant,
  MarketDataSourceSchema as ContractMarketDataSourceSchema,
  BienTypeSchema as ContractBienTypeSchema,
  StructureKindSchema as ContractStructureKindSchema,
  ElementKindSchema as ContractElementKindSchema,
  MeasurementUnitSchema as ContractMeasurementUnitSchema,
  QuantityInputModeSchema as ContractQuantityInputModeSchema,
  QuantityFormulaCodeSchema as ContractQuantityFormulaCodeSchema,
  FormulaStatusSchema as ContractFormulaStatusSchema,
  GeographicScopeTypeSchema as ContractGeographicScopeTypeSchema,
  CatalogVersionStatusSchema as ContractCatalogVersionStatusSchema,
  CostResolutionModeSchema as ContractCostResolutionModeSchema,
  TariffVariationTypeSchema as ContractTariffVariationTypeSchema,
  type MandantType as ContractMandantType,
  type CaseStatus,
  type CaseScenario,
  type MarketDataSource as ContractMarketDataSource,
  type BienType as ContractBienType,
  type StructureKind as ContractStructureKind,
  type ElementKind as ContractElementKind,
  type MeasurementUnit as ContractMeasurementUnit,
  type QuantityInputMode as ContractQuantityInputMode,
  type QuantityFormulaCode as ContractQuantityFormulaCode,
  type FormulaStatus as ContractFormulaStatus,
  type GeographicScopeType as ContractGeographicScopeType,
  type CatalogVersionStatus as ContractCatalogVersionStatus,
  type CostResolutionMode as ContractCostResolutionMode,
  type TariffVariationType as ContractTariffVariationType,
} from '@derliz/contracts';

/**
 * Web-side view of the DERLIZ API contract (all routes under /api/v1).
 *
 * These schemas mirror the API's response DTOs so the UI can parse and type server payloads. The
 * server remains the single source of truth for every business rule — we do NOT re-encode workflow
 * or permission logic here.
 *
 * LOT 3.5: the shared business ENUMS (mandant type, case status, case scenario) are imported from
 * `@derliz/contracts` (the single source of truth) and re-exported under the web's local names. Only
 * DTO shapes and French DISPLAY LABELS (UI concern) are defined locally — a code is never a label.
 */

// --- Shared envelopes ---------------------------------------------------------

export const ApiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    correlationId: z.string().optional(),
    timestamp: z.string().optional(),
    details: z.array(z.string()).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>;

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalPages: z.number(),
  });
}
export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// --- Auth ---------------------------------------------------------------------

export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
});
export type AuthTokens = z.infer<typeof AuthTokensSchema>;

export const CurrentUserSchema = z.object({
  userId: z.string(),
  email: z.string(),
  roles: z.array(z.string()),
});
export type CurrentUser = z.infer<typeof CurrentUserSchema>;

// --- Mandants -----------------------------------------------------------------

// Re-exported from the single source of truth (@derliz/contracts).
export const MandantTypeSchema = ContractMandantTypeSchema;
export type MandantType = ContractMandantType;

export const MandantCoordonneesSchema = z.object({
  telephone: z.string().optional(),
  email: z.string().optional(),
  adresse: z.string().optional(),
  contact: z.string().optional(),
});
export type MandantCoordonnees = z.infer<typeof MandantCoordonneesSchema>;

export const MandantDtoSchema = z.object({
  id: z.string(),
  nom: z.string(),
  type: MandantTypeSchema,
  coordonnees: MandantCoordonneesSchema.nullable().optional(),
  isActive: z.boolean(),
  createdById: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MandantDto = z.infer<typeof MandantDtoSchema>;

// --- Dossiers -----------------------------------------------------------------

// Re-exported from the single source of truth (@derliz/contracts). The web keeps the local names
// (DossierStatut/DossierScenario) but the values & types now come from the shared package.
export const DossierStatutSchema = CaseStatusSchema;
export type DossierStatut = CaseStatus;

export const DossierScenarioSchema = CaseScenarioSchema;
export type DossierScenario = CaseScenario;

/** LOT 9A — PV business variant. Values come from the shared contract, never redeclared here. */
export const PvVariantSchema = SharedPvVariantSchema;
export type PvVariant = SharedPvVariant;

/**
 * LOT 9A — the variants the SERVER offers for a case, with their prerequisites and readiness.
 * Everything here is server-authored: the Web renders it and derives nothing.
 */
export const PvVariantOptionSchema = z.object({
  variant: PvVariantSchema,
  label: z.string(),
  publishes: z.string(),
  rule: z.string(),
  documentType: z.string().nullable(),
  available: z.boolean(),
  unavailableReason: z.string().nullable(),
  prerequisites: z.array(z.string()),
  selected: z.boolean(),
});
export const PvVariantsDtoSchema = z.object({
  scenario: DossierScenarioSchema,
  selected: PvVariantSchema.nullable(),
  selectable: z.boolean(),
  policyId: z.string(),
  policyVersion: z.string(),
  variants: z.array(PvVariantOptionSchema),
});
export type PvVariantOption = z.infer<typeof PvVariantOptionSchema>;
export type PvVariantsDto = z.infer<typeof PvVariantsDtoSchema>;

export const DossierDtoSchema = z.object({
  id: z.string(),
  numero: z.string(),
  prefix: z.string(),
  year: z.number(),
  sequence: z.number(),
  mandantId: z.string(),
  mandantNom: z.string(),
  ownerId: z.string().nullable().optional(),
  createdById: z.string(),
  statut: DossierStatutSchema,
  scenario: DossierScenarioSchema,
  /** LOT 9A: the SELECTED PV variant (PV_SEUL only). Null = nothing selected — never a default. */
  pvVariant: PvVariantSchema.nullable().optional(),
  objet: z.string().nullable().optional(),
  availableTransitions: z.array(DossierStatutSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DossierDto = z.infer<typeof DossierDtoSchema>;

export const TransitionDtoSchema = z.object({
  id: z.string(),
  dossierId: z.string(),
  fromStatut: DossierStatutSchema.nullable().optional(),
  toStatut: DossierStatutSchema,
  byUserId: z.string(),
  justification: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type TransitionDto = z.infer<typeof TransitionDtoSchema>;

// --- LOT 4 : structure immobilière (Bien → Structure → Niveau → Élément) -------

// Enums re-exported from the single source of truth (@derliz/contracts).
export const BienTypeSchema = ContractBienTypeSchema;
export type BienType = ContractBienType;

export const StructureKindSchema = ContractStructureKindSchema;
export type StructureKind = ContractStructureKind;

export const ElementKindSchema = ContractElementKindSchema;
export type ElementKind = ContractElementKind;

export const MeasurementUnitSchema = ContractMeasurementUnitSchema;
export type MeasurementUnit = ContractMeasurementUnit;

export const QuantityInputModeSchema = ContractQuantityInputModeSchema;
export type QuantityInputMode = ContractQuantityInputMode;

export const QuantityFormulaCodeSchema = ContractQuantityFormulaCodeSchema;
export type QuantityFormulaCode = ContractQuantityFormulaCode;

export const FormulaStatusSchema = ContractFormulaStatusSchema;
export type FormulaStatus = ContractFormulaStatus;

export const BienDtoSchema = z.object({
  id: z.string(),
  dossierId: z.string(),
  designation: z.string(),
  type: BienTypeSchema,
  reference: z.string().nullable().optional(),
  /** Decimal serialised as a string — never manipulated numerically in the web. */
  superficie: z.string().nullable().optional(),
  typeZone: z.string().nullable().optional(),
  affectation: z.string().nullable().optional(),
  titreJouissance: z.string().nullable().optional(),
  /// LOT 9D — titulaire du titre, distinct du mandant (design §11).
  nomSurTitre: z.string().nullable().optional(),
  ordre: z.number(),
  createdById: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BienDto = z.infer<typeof BienDtoSchema>;

// --- LOT 8C: market data (REQ-F-061) ------------------------------------------
export const MarketDataSourceSchema = ContractMarketDataSourceSchema;
export type MarketDataSource = ContractMarketDataSource;

export const MarketDataDtoSchema = z.object({
  id: z.string(),
  dossierId: z.string(),
  source: MarketDataSourceSchema,
  localisation: z.string(),
  /** Decimals serialised as strings — never manipulated numerically in the web. */
  superficieM2: z.string().nullable().optional(),
  prix: z.string().nullable().optional(),
  prixM2: z.string().nullable().optional(),
  dateReleve: z.string().nullable().optional(),
  observation: z.string().nullable().optional(),
  ordre: z.number(),
  createdById: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MarketDataDto = z.infer<typeof MarketDataDtoSchema>;

export const StructureDtoSchema = z.object({
  id: z.string(),
  bienId: z.string(),
  dossierId: z.string(),
  libelle: z.string(),
  type: z.string().nullable().optional(),
  kind: StructureKindSchema,
  ordre: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type StructureDto = z.infer<typeof StructureDtoSchema>;

export const NiveauDtoSchema = z.object({
  id: z.string(),
  structureId: z.string(),
  dossierId: z.string(),
  libelle: z.string(),
  ordre: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type NiveauDto = z.infer<typeof NiveauDtoSchema>;

export const ElementDtoSchema = z.object({
  id: z.string(),
  bienId: z.string(),
  niveauId: z.string().nullable().optional(),
  dossierId: z.string(),
  kind: ElementKindSchema,
  libelle: z.string(),
  description: z.string().nullable().optional(),
  spaceType: z.string().nullable().optional(),
  ordre: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ElementDto = z.infer<typeof ElementDtoSchema>;

export const QuantityDtoSchema = z.object({
  id: z.string(),
  elementId: z.string(),
  dossierId: z.string(),
  inputMode: QuantityInputModeSchema,
  formulaId: z.string().nullable().optional(),
  formulaCode: QuantityFormulaCodeSchema.nullable().optional(),
  formulaVersion: z.number().nullable().optional(),
  parameters: z.record(z.string(), z.unknown()).nullable().optional(),
  directQuantity: z.string().nullable().optional(),
  /** Server-computed quantity (decimal string) — the web NEVER computes formulas. */
  calculatedQuantity: z.string(),
  measurementUnit: MeasurementUnitSchema,
  source: z.string().nullable().optional(),
  justification: z.string().nullable().optional(),
  enteredById: z.string(),
  validatedById: z.string().nullable().optional(),
  validatedAt: z.string().nullable().optional(),
  snapshotHash: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type QuantityDto = z.infer<typeof QuantityDtoSchema>;

export const QuantityPreviewSchema = z.object({
  calculatedQuantity: z.string(),
  measurementUnit: MeasurementUnitSchema,
});
export type QuantityPreview = z.infer<typeof QuantityPreviewSchema>;

/** Declarative parameter descriptor driving the dynamic quantity form. */
export const FormulaParameterKindSchema = z.enum([
  'DECIMAL',
  'INT',
  'DECIMAL_LIST',
  'RECTANGLE_LIST',
]);
export type FormulaParameterKind = z.infer<typeof FormulaParameterKindSchema>;

export const FormulaParameterDefSchema = z.object({
  name: z.string(),
  kind: FormulaParameterKindSchema,
  unit: z.string().nullable().optional(),
  required: z.boolean().optional(),
});
export type FormulaParameterDef = z.infer<typeof FormulaParameterDefSchema>;

export const FormulaParametersSchemaSchema = z.object({
  outputUnit: MeasurementUnitSchema,
  parameters: z.array(FormulaParameterDefSchema),
});
export type FormulaParametersSchema = z.infer<typeof FormulaParametersSchemaSchema>;

export const QuantityFormulaDtoSchema = z.object({
  id: z.string(),
  code: QuantityFormulaCodeSchema,
  version: z.number(),
  label: z.string(),
  description: z.string().nullable().optional(),
  parametersSchema: FormulaParametersSchemaSchema.nullable().optional(),
  source: z.string().nullable().optional(),
  status: FormulaStatusSchema,
  isSystem: z.boolean(),
  isActive: z.boolean(),
  createdById: z.string().nullable().optional(),
  validatedById: z.string().nullable().optional(),
  submittedAt: z.string().nullable().optional(),
  validatedAt: z.string().nullable().optional(),
  validFrom: z.string().nullable().optional(),
  validTo: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type QuantityFormulaDto = z.infer<typeof QuantityFormulaDtoSchema>;

// --- LOT 5 : référentiels (DEC-005) --------------------------------------------

// Enums re-exported from the single source of truth (@derliz/contracts).
export const GeographicScopeTypeSchema = ContractGeographicScopeTypeSchema;
export type GeographicScopeType = ContractGeographicScopeType;

export const CatalogVersionStatusSchema = ContractCatalogVersionStatusSchema;
export type CatalogVersionStatus = ContractCatalogVersionStatus;

export const CostResolutionModeSchema = ContractCostResolutionModeSchema;
export type CostResolutionMode = ContractCostResolutionMode;

export const TariffVariationTypeSchema = ContractTariffVariationTypeSchema;
export type TariffVariationType = ContractTariffVariationType;

/**
 * One geographic referential entry (pays / ville / quartier / catégorie de zone).
 * The parent ids are populated depending on the entity kind.
 */
export const GeoEntityDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  countryId: z.string().nullable().optional(),
  cityId: z.string().nullable().optional(),
  zoneCategoryId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type GeoEntityDto = z.infer<typeof GeoEntityDtoSchema>;

/** Referential measurement unit (read-only: M2 / ML / U / M3). */
export const MeasurementUnitRefDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});
export type MeasurementUnitRefDto = z.infer<typeof MeasurementUnitRefDtoSchema>;

export const TechnicalCategoryDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type TechnicalCategoryDto = z.infer<typeof TechnicalCategoryDtoSchema>;

export const ComponentTypeDtoSchema = z.object({
  id: z.string(),
  categoryId: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional(),
  /** Allowed measurement units for this component type. */
  units: z.array(MeasurementUnitRefDtoSchema).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type ComponentTypeDto = z.infer<typeof ComponentTypeDtoSchema>;

export const TariffGroupMemberDtoSchema = z.object({
  componentTypeId: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
});
export type TariffGroupMemberDto = z.infer<typeof TariffGroupMemberDtoSchema>;

export const TariffGroupDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional(),
  members: z.array(TariffGroupMemberDtoSchema).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type TariffGroupDto = z.infer<typeof TariffGroupDtoSchema>;

export const CostCatalogDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type CostCatalogDto = z.infer<typeof CostCatalogDtoSchema>;

export const CatalogVersionDtoSchema = z.object({
  id: z.string(),
  catalogId: z.string(),
  versionNumber: z.number().optional(),
  status: CatalogVersionStatusSchema,
  validFrom: z.string(),
  validTo: z.string().nullable().optional(),
  /** Populated on the version-detail endpoint. */
  costsCount: z.number().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type CatalogVersionDto = z.infer<typeof CatalogVersionDtoSchema>;

export const UnitCostDtoSchema = z.object({
  id: z.string(),
  catalogVersionId: z.string().optional(),
  scopeType: GeographicScopeTypeSchema,
  scopeId: z.string(),
  componentTypeId: z.string(),
  measurementUnitId: z.string(),
  /** Decimal serialised as a string — never manipulated numerically in the web. */
  amount: z.string(),
  validFrom: z.string(),
  validTo: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type UnitCostDto = z.infer<typeof UnitCostDtoSchema>;

export const BulkAssignmentResultDtoSchema = z.object({ createdCount: z.number() }).passthrough();
export type BulkAssignmentResultDto = z.infer<typeof BulkAssignmentResultDtoSchema>;

/** One duplication line (creatable or conflicting) — shape kept permissive on purpose. */
export const DuplicationLineDtoSchema = z
  .object({
    componentTypeId: z.string().optional(),
    componentTypeCode: z.string().optional(),
    amount: z.string().optional(),
    measurementUnitId: z.string().optional(),
    reason: z.string().optional(),
    message: z.string().optional(),
  })
  .passthrough();
export type DuplicationLineDto = z.infer<typeof DuplicationLineDtoSchema>;

export const DuplicationPreviewDtoSchema = z.object({
  creatable: z.array(DuplicationLineDtoSchema),
  conflicts: z.array(DuplicationLineDtoSchema),
});
export type DuplicationPreviewDto = z.infer<typeof DuplicationPreviewDtoSchema>;

export const DuplicationApplyResultDtoSchema = z
  .object({ createdCount: z.number().optional() })
  .passthrough();
export type DuplicationApplyResultDto = z.infer<typeof DuplicationApplyResultDtoSchema>;

export const CoefficientTypeDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type CoefficientTypeDto = z.infer<typeof CoefficientTypeDtoSchema>;

export const CoefficientSetDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type CoefficientSetDto = z.infer<typeof CoefficientSetDtoSchema>;

export const CoefficientValueDtoSchema = z.object({
  coefficientTypeId: z.string(),
  code: z.string().optional(),
  /** Decimal serialised as a string. */
  value: z.string(),
  source: z.string().nullable().optional(),
});
export type CoefficientValueDto = z.infer<typeof CoefficientValueDtoSchema>;

export const CoefficientVersionDtoSchema = z.object({
  id: z.string(),
  setId: z.string().optional(),
  versionNumber: z.number().optional(),
  status: CatalogVersionStatusSchema,
  validFrom: z.string(),
  validTo: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  /** Populated on the version-detail endpoint. */
  values: z.array(CoefficientValueDtoSchema).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type CoefficientVersionDto = z.infer<typeof CoefficientVersionDtoSchema>;

export const CoefficientResolutionDtoSchema = z.object({
  setId: z.string(),
  setVersion: z.union([z.number(), z.string()]),
  coefficients: z.array(
    z.object({
      code: z.string(),
      value: z.string(),
      source: z.string().nullable().optional(),
    }),
  ),
});
export type CoefficientResolutionDto = z.infer<typeof CoefficientResolutionDtoSchema>;

export const CostResolutionPreviewDtoSchema = z.object({
  // The preview may resolve to BLOCKED (no applicable cost) in addition to the persisted modes.
  mode: z.union([CostResolutionModeSchema, z.literal('BLOCKED')]),
  fallbackLevel: z.string().nullable().optional(),
  unitCost: z
    .object({
      amount: z.string(),
      scopeType: GeographicScopeTypeSchema,
    })
    .passthrough()
    .nullable()
    .optional(),
  catalogId: z.string().optional(),
  catalogVersion: z.union([z.number(), z.string()]).optional(),
  /** Server-decided flag: a FALLBACK must be confirmed explicitly, never silently. */
  requiresConfirmation: z.boolean(),
});
export type CostResolutionPreviewDto = z.infer<typeof CostResolutionPreviewDtoSchema>;

export const CostSnapshotDtoSchema = z
  .object({
    id: z.string().optional(),
    resolutionMode: CostResolutionModeSchema,
    fallbackLevel: z.string().nullable().optional(),
    referenceCost: z.string().nullable().optional(),
    appliedCost: z.string(),
    snapshotHash: z.string(),
  })
  .passthrough();
export type CostSnapshotDto = z.infer<typeof CostSnapshotDtoSchema>;

export const ImportInvalidRowDtoSchema = z.object({
  line: z.number(),
  errors: z.array(z.string()),
});
export type ImportInvalidRowDto = z.infer<typeof ImportInvalidRowDtoSchema>;

export const ImportReportDtoSchema = z.object({
  totalRows: z.number(),
  validRows: z.array(z.unknown()),
  invalidRows: z.array(ImportInvalidRowDtoSchema),
  duplicates: z.array(z.unknown()),
  conflicts: z.array(z.unknown()),
  createdCount: z.number().optional(),
});
export type ImportReportDto = z.infer<typeof ImportReportDtoSchema>;

// --- LOT 6B : calculs (calc runs) ----------------------------------------------

/** The four valuation methods the engine can run. */
export const CalcMethodCodeSchema = z.enum(['COUT', 'COMPARAISON', 'CAPITALISATION', 'MOYENNE']);
export type CalcMethodCode = z.infer<typeof CalcMethodCodeSchema>;

/** Calc-run lifecycle status (server-owned; the web only displays and gates on it). */
export const CalcRunStatusSchema = z.enum([
  'BROUILLON',
  'PREPARE',
  'BLOQUE',
  'CALCULE',
  'EN_REVUE',
  'VALIDE',
  'FINALISE',
  'REMPLACE',
  'ANNULE',
]);
export type CalcRunStatus = z.infer<typeof CalcRunStatusSchema>;

/** One coefficient applied to a method (all values are server-decided decimal strings). */
export const CalcCoefficientDtoSchema = z.object({
  coefficientTypeCode: z.string(),
  applicability: z.string(),
  referenceValue: z.string(),
  appliedValue: z.string(),
  overrideApplied: z.boolean(),
  overrideJustification: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
});
export type CalcCoefficientDto = z.infer<typeof CalcCoefficientDtoSchema>;

export const MethodRunDtoSchema = z.object({
  id: z.string(),
  methodCode: z.string(),
  methodRuleVersion: z.string(),
  /** Server-computed decimal strings — the web NEVER recomputes a value. */
  rawValue: z.string(),
  roundedValue: z.string(),
  publishedValue: z.string(),
  finalizable: z.boolean(),
  methodHash: z.string(),
  intermediateResults: z.unknown(),
  coefficients: z.array(CalcCoefficientDtoSchema),
});
export type MethodRunDto = z.infer<typeof MethodRunDtoSchema>;

export const CalcLineDtoSchema = z.object({
  lineRef: z.string(),
  designation: z.string(),
  kind: z.string(),
  totalGroup: z.number(),
  contributionMode: z.string(),
  quantity: z.string().nullable().optional(),
  unitCost: z.string().nullable().optional(),
  vetustePct: z.string().nullable().optional(),
  forfaitValue: z.string().nullable().optional(),
  value: z.string(),
  ambiguous: z.boolean(),
});
export type CalcLineDto = z.infer<typeof CalcLineDtoSchema>;

export const CalcCostDtoSchema = z.object({
  lineRef: z.string(),
  referenceCost: z.string(),
  appliedCost: z.string(),
  resolutionMode: z.string(),
  fallbackLevel: z.string().nullable().optional(),
  snapshotHash: z.string(),
});
export type CalcCostDto = z.infer<typeof CalcCostDtoSchema>;

export const CalcIssueDtoSchema = z.object({
  code: z.string(),
  message: z.string(),
});
export type CalcIssueDto = z.infer<typeof CalcIssueDtoSchema>;

export const CalcAdjustmentDtoSchema = z.object({
  automaticValue: z.string(),
  publishedValue: z.string(),
  justification: z.string(),
  createdAt: z.string(),
});
export type CalcAdjustmentDto = z.infer<typeof CalcAdjustmentDtoSchema>;

export const CalcRunDtoSchema = z.object({
  id: z.string(),
  dossierId: z.string(),
  valuationDate: z.string(),
  engineVersion: z.string(),
  status: CalcRunStatusSchema,
  finalizable: z.boolean(),
  requestedMethods: z.array(z.string()),
  inputSnapshotHash: z.string(),
  calculationHash: z.string().nullable(),
  /** Server-published final value (decimal string) — never computed client-side. */
  finalValue: z.string().nullable(),
  roundingPolicyId: z.string(),
  roundingPolicyVersion: z.string(),
  contributionModelId: z.string(),
  contributionModelVersion: z.string(),
  coefficientMatrixId: z.string(),
  coefficientMatrixVersion: z.string(),
  supersedesCalcRunId: z.string().nullable(),
  createdById: z.string(),
  createdAt: z.string(),
  validatedById: z.string().nullable(),
  validatedAt: z.string().nullable(),
  finalizedAt: z.string().nullable(),
  methods: z.array(MethodRunDtoSchema),
  lines: z.array(CalcLineDtoSchema),
  costSnapshots: z.array(CalcCostDtoSchema),
  warnings: z.array(CalcIssueDtoSchema),
  errors: z.array(CalcIssueDtoSchema),
  adjustments: z.array(CalcAdjustmentDtoSchema),
});
export type CalcRunDto = z.infer<typeof CalcRunDtoSchema>;

/** List summary — a subset of the full run returned by the paginated list endpoint. */
export const CalcRunSummaryDtoSchema = z.object({
  id: z.string(),
  dossierId: z.string(),
  valuationDate: z.string(),
  status: CalcRunStatusSchema,
  finalizable: z.boolean(),
  requestedMethods: z.array(z.string()),
  engineVersion: z.string(),
  calculationHash: z.string().nullable(),
  finalValue: z.string().nullable(),
  supersedesCalcRunId: z.string().nullable(),
  createdById: z.string(),
  createdAt: z.string(),
  validatedById: z.string().nullable(),
  finalizedAt: z.string().nullable(),
});
export type CalcRunSummaryDto = z.infer<typeof CalcRunSummaryDtoSchema>;

/** The list envelope is {data, page, pageSize, total} — distinct from the `pageSchema` one. */
export const CalcRunListDtoSchema = z.object({
  data: z.array(CalcRunSummaryDtoSchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
});
export type CalcRunListDto = z.infer<typeof CalcRunListDtoSchema>;

// --- LOT 7A : documents (DOCX generation) --------------------------------------

/** The four document types the platform can generate. */
export const DocumentTypeSchema = z.enum([
  'PV_BATI',
  'PV_EXPERTISE_COMPLETE',
  'RAPPORT_EXPERTISE',
  'DOCUMENT_METHODE_SPECIFIQUE',
]);
export type DocumentType = z.infer<typeof DocumentTypeSchema>;

/** Document lifecycle status (server-owned; the web only displays and gates on it). */
export const DocumentStatusSchema = z.enum([
  'BROUILLON',
  'DOCX_GENERATED',
  'PDFA_VALIDATION_PENDING',
  'PDFA_VALID',
  'PDFA_VALIDATION_FAILED',
  'FINALISE',
  'ARCHIVE',
  'REMPLACE',
  'ANNULE',
]);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

/** PDF/A pipeline status — produced & validated in LOT 7B (NOT_GENERATED in LOT 7A). */
export const PdfaStatusSchema = z.enum([
  'NOT_GENERATED',
  'GENERATED',
  'VALIDATION_FAILED',
  'VALID',
  'ARCHIVED',
]);
export type PdfaStatus = z.infer<typeof PdfaStatusSchema>;

/** Template version lifecycle (create → BROUILLON, activate → ACTIF, previous → ARCHIVE). */
export const TemplateVersionStatusSchema = z.enum(['BROUILLON', 'ACTIF', 'ARCHIVE']);
export type TemplateVersionStatus = z.infer<typeof TemplateVersionStatusSchema>;

/**
 * A generated document. Every hash & published value here is SERVER-computed — the web
 * only submits generation intent and DISPLAYS the authoritative values it gets back.
 */
export const DocumentDtoSchema = z.object({
  id: z.string(),
  dossierId: z.string(),
  calcRunId: z.string(),
  templateVersionId: z.string(),
  documentType: DocumentTypeSchema,
  version: z.number(),
  displayLabel: z.string(),
  status: DocumentStatusSchema,
  pdfaStatus: PdfaStatusSchema,
  publishedValue: z.string().nullable(),
  publishedValueSource: z.string().nullable(),
  publishedValuePolicyId: z.string(),
  publishedValuePolicyVersion: z.string(),
  supersedesDocumentId: z.string().nullable(),
  inheritsFromDocumentId: z.string().nullable().optional(),
  manifestHash: z.string().nullable(),
  docxSha256: z.string().nullable(),
  generatedById: z.string(),
  generatedAt: z.string().nullable(),
  finalizedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type DocumentDto = z.infer<typeof DocumentDtoSchema>;

/** List summary — a subset of the full document returned by the paginated list endpoint. */
export const DocumentSummaryDtoSchema = z.object({
  id: z.string(),
  documentType: DocumentTypeSchema,
  version: z.number(),
  status: DocumentStatusSchema,
  pdfaStatus: PdfaStatusSchema,
  publishedValue: z.string().nullable(),
  publishedValueSource: z.string().nullable(),
  manifestHash: z.string().nullable(),
  supersedesDocumentId: z.string().nullable(),
  generatedById: z.string(),
  createdAt: z.string(),
});
export type DocumentSummaryDto = z.infer<typeof DocumentSummaryDtoSchema>;

/** The list envelope is {data, page, pageSize, total} — matching the calc-run list one. */
export const DocumentListDtoSchema = z.object({
  data: z.array(DocumentSummaryDtoSchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
});
export type DocumentListDto = z.infer<typeof DocumentListDtoSchema>;

// --- LOT B3 : portail client (lecture seule) ---------------------------------
// Formes MINIMALES renvoyées par `/portal/*` : le serveur ne divulgue au client que l'essentiel
// (aucun id interne owner/calcRun/policy). Statut/scénario/type restent des chaînes (découplage).
export const PortalDossierSummarySchema = z.object({
  id: z.string(),
  numero: z.string(),
  statut: z.string(),
  scenario: z.string(),
  objet: z.string().nullable(),
  mandantNom: z.string(),
  createdAt: z.string(),
});
export type PortalDossierSummary = z.infer<typeof PortalDossierSummarySchema>;

export const PortalDocumentSummarySchema = z.object({
  id: z.string(),
  documentType: z.string(),
  displayLabel: z.string(),
  publishedValue: z.string().nullable(),
  finalizedAt: z.string().nullable(),
});
export type PortalDocumentSummary = z.infer<typeof PortalDocumentSummarySchema>;

export const PortalDossierDetailSchema = PortalDossierSummarySchema.extend({
  documents: z.array(PortalDocumentSummarySchema),
});
export type PortalDossierDetail = z.infer<typeof PortalDossierDetailSchema>;

// --- Tableau de bord (dashboard) ---------------------------------------------
// Formes renvoyées par `/dashboard/summary`. Toutes les valeurs sont des agrégations LIVE côté
// serveur (jamais figées) ; le périmètre (GLOBAL vs OWNER) est décidé côté serveur. Les codes
// statut/scénario/type de document restent des chaînes (découplage du référentiel serveur).
export const DashboardStatutCountSchema = z.object({
  statut: z.string(),
  count: z.number(),
});

export const DashboardScenarioCountSchema = z.object({
  scenario: z.string(),
  count: z.number(),
});

export const DashboardDocumentStatusCountSchema = z.object({
  status: z.string(),
  count: z.number(),
});

export const DashboardRecentDossierSchema = z.object({
  id: z.string(),
  numero: z.string(),
  statut: z.string(),
  mandantNom: z.string(),
  createdAt: z.string(),
});

export const DashboardSummarySchema = z.object({
  scope: z.enum(['GLOBAL', 'OWNER']),
  totalDossiers: z.number(),
  byStatut: z.array(DashboardStatutCountSchema),
  byScenario: z.array(DashboardScenarioCountSchema),
  recentDossiers: z.array(DashboardRecentDossierSchema),
  documents: z.object({
    total: z.number(),
    byStatus: z.array(DashboardDocumentStatusCountSchema),
  }),
  createdLast30Days: z.number(),
});
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;
export type DashboardRecentDossier = z.infer<typeof DashboardRecentDossierSchema>;

/** Read-only manifest response — the web NEVER builds the manifest or its hash. */
export const DocumentManifestDtoSchema = z.object({
  schemaVersion: z.string(),
  manifestHash: z.string(),
  manifest: z.unknown(),
});
export type DocumentManifestDto = z.infer<typeof DocumentManifestDtoSchema>;

/** One document generation attempt (SUCCESS / BLOCKED) — audit trail for the dossier. */
export const GenerationRunDtoSchema = z.object({
  id: z.string(),
  documentId: z.string().nullable(),
  calcRunId: z.string(),
  status: z.string(),
  docxSha256: z.string().nullable(),
  manifestHash: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
});
export type GenerationRunDto = z.infer<typeof GenerationRunDtoSchema>;

// --- LOT 7B : chaîne archivistique PDF/A (pipeline) -----------------------------

/**
 * The pipeline RUN status (server-owned). Every value is decided by the server's archival pipeline
 * (LibreOffice → veraPDF → Ghostscript). The web NEVER converts, validates or re-derives any of these.
 */
export const PipelineStatusSchema = z.enum([
  'PENDING',
  'PDF_CONVERSION_STARTED',
  'PDF_GENERATED',
  'PDFA_CONVERSION_STARTED',
  'PDFA_GENERATED',
  'PDFA_VALIDATION_STARTED',
  'PDFA_VALID',
  'PDFA_VALIDATION_FAILED',
  'GHOSTSCRIPT_REMEDIATION_STARTED',
  'REMEDIATED_PDFA_GENERATED',
  'PDFA_REVALIDATION_STARTED',
  'PDFA_VALIDATION_FAILED_FINAL',
  'FAILED',
]);
export type PipelineStatus = z.infer<typeof PipelineStatusSchema>;

/** One persisted pipeline status transition (append-only history). */
export const PipelineTransitionDtoSchema = z.object({
  fromStatus: PipelineStatusSchema,
  toStatus: PipelineStatusSchema,
  createdAt: z.string(),
});
export type PipelineTransitionDto = z.infer<typeof PipelineTransitionDtoSchema>;

/** A veraPDF validation result — the SOLE conformance authority (server-produced). */
export const PdfaValidationDtoSchema = z.object({
  veraPdfVersion: z.string(),
  profile: z.string(),
  result: z.boolean(),
  errorsCount: z.number(),
  warningsCount: z.number(),
  durationMs: z.number().nullable().optional(),
  isRevalidation: z.boolean(),
  ghostscriptUsed: z.boolean(),
  createdAt: z.string(),
});
export type PdfaValidationDto = z.infer<typeof PdfaValidationDtoSchema>;

/** The latest archival pipeline run + its transitions + veraPDF validations (read model). */
export const PipelineRunDtoSchema = z.object({
  id: z.string(),
  status: PipelineStatusSchema,
  attempt: z.number(),
  ghostscriptUsed: z.boolean(),
  conversionDurationMs: z.number().nullable().optional(),
  validationDurationMs: z.number().nullable().optional(),
  error: z.string().nullable().optional(),
  createdAt: z.string(),
  transitions: z.array(PipelineTransitionDtoSchema),
  validations: z.array(PdfaValidationDtoSchema),
});
export type PipelineRunDto = z.infer<typeof PipelineRunDtoSchema>;

/** GET /documents/:id/pipeline — global document/pdfa status + the latest run (or null if never run). */
export const PipelineStatusDtoSchema = z.object({
  pdfaStatus: PdfaStatusSchema,
  documentStatus: DocumentStatusSchema,
  run: PipelineRunDtoSchema.nullable(),
});
export type PipelineStatusDto = z.infer<typeof PipelineStatusDtoSchema>;

/** POST /documents/:id/pipeline/{start,retry} — the enqueued run id (+ whether it ran synchronously). */
export const StartPipelineDtoSchema = z.object({
  pipelineRunId: z.string(),
  executedSync: z.boolean(),
});
export type StartPipelineDto = z.infer<typeof StartPipelineDtoSchema>;

export const TemplateVersionDtoSchema = z.object({
  id: z.string(),
  version: z.number(),
  status: TemplateVersionStatusSchema,
  docxSha256: z.string(),
  publishedValuePolicyId: z.string(),
  publishedValuePolicyVersion: z.string(),
  configuredMethod: CalcMethodCodeSchema.nullable(),
  activatedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type TemplateVersionDto = z.infer<typeof TemplateVersionDtoSchema>;

export const TemplateDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  documentType: DocumentTypeSchema,
  name: z.string(),
  description: z.string().nullable(),
  createdAt: z.string(),
  versions: z.array(TemplateVersionDtoSchema),
});
export type TemplateDto = z.infer<typeof TemplateDtoSchema>;

// --- Human-readable French labels ---------------------------------------------

export const MANDANT_TYPE_LABELS: Record<MandantType, string> = {
  PARTICULIER: 'Particulier',
  BANQUE: 'Banque',
  ASSURANCE: 'Assurance',
  ADMINISTRATION: 'Administration',
  AUTRE: 'Autre',
};

export const DOSSIER_STATUT_LABELS: Record<DossierStatut, string> = {
  CREE: 'Créé',
  EN_COURS: 'En cours',
  DONNEES_COLLECTEES: 'Données collectées',
  CALCULE: 'Calculé',
  GENERE: 'Généré',
  VERIFIE: 'Vérifié',
  FINALISE: 'Finalisé',
  ARCHIVE: 'Archivé',
};

export const DOSSIER_SCENARIO_LABELS: Record<DossierScenario, string> = {
  PV_SEUL: 'PV seul',
  RAPPORT_SEUL: 'Rapport seul',
  PV_RAPPORT: 'PV + Rapport',
};

/** French labels for the 4 market-data sources (REQ-F-061). UI concern; the code is never a label. */
export const MARKET_DATA_SOURCE_LABELS: Record<MarketDataSource, string> = {
  DGI_FISCAL: 'DGI / valeurs fiscales',
  AGENCE_IMMOBILIERE: 'Agences immobilières',
  ENQUETE_TERRAIN: 'Enquêtes terrain',
  BASE_INTERNE: 'Base interne DERLIZ',
};

export const BIEN_TYPE_LABELS: Record<BienType, string> = {
  TERRAIN_NU: 'Terrain nu',
  TERRAIN_BATI: 'Terrain bâti',
  IMMEUBLE: 'Immeuble',
  AUTRE: 'Autre',
};

export const STRUCTURE_KIND_LABELS: Record<StructureKind, string> = {
  PRINCIPAL: 'Bâtiment principal',
  ANNEXE: 'Annexe',
};

export const ELEMENT_KIND_LABELS: Record<ElementKind, string> = {
  ESPACE: 'Espace',
  OUVRAGE: 'Ouvrage extérieur',
  PLUS_VALUE: 'Plus-value',
};

export const MEASUREMENT_UNIT_LABELS: Record<MeasurementUnit, string> = {
  M2: 'm²',
  ML: 'ml',
  U: 'unité',
  M3: 'm³',
};

export const QUANTITY_INPUT_MODE_LABELS: Record<QuantityInputMode, string> = {
  STRUCTURED_FORMULA: 'Formule structurée',
  DIRECT_QUANTITY: 'Quantité directe',
};

/**
 * French display labels for the formula parameter NAMES coming from
 * `parametersSchema` (the wire keeps the technical names, e.g. `length`).
 * Unknown names fall back to the technical name.
 */
export const FORMULA_PARAM_LABELS: Record<string, string> = {
  length: 'Longueur',
  width: 'Largeur',
  height: 'Hauteur',
  count: 'Nombre',
  values: 'Valeurs',
  rectangles: 'Rectangles',
  sideA: 'Côté A',
  sideB: 'Côté B',
  openings: 'Ouvertures',
  quantity: 'Quantité',
  components: 'Composantes',
};

export function formulaParamLabel(name: string): string {
  return FORMULA_PARAM_LABELS[name] ?? name;
}

/** Unit suffix for a single parameter input (metres / units). */
export const FORMULA_PARAM_UNIT_LABELS: Record<string, string> = {
  M: 'm',
  U: 'unité',
};

// --- LOT 5 : French labels ------------------------------------------------------

export const GEO_SCOPE_TYPE_LABELS: Record<GeographicScopeType, string> = {
  COUNTRY: 'Pays',
  CITY: 'Ville',
  DISTRICT: 'Quartier',
  ZONE_CATEGORY: 'Catégorie de zone',
};

export const CATALOG_VERSION_STATUS_LABELS: Record<CatalogVersionStatus, string> = {
  BROUILLON: 'Brouillon',
  EN_VALIDATION: 'En validation',
  ACTIF: 'Actif',
  EXPIRE: 'Expiré',
  ARCHIVE: 'Archivé',
};

export const COST_RESOLUTION_MODE_LABELS: Record<CostResolutionMode, string> = {
  EXACT_MATCH: 'Correspondance exacte',
  FALLBACK: 'Repli (fallback)',
  MANUAL_OVERRIDE: 'Coût manuel (override)',
};

export const TARIFF_VARIATION_TYPE_LABELS: Record<TariffVariationType, string> = {
  PERCENT: 'Pourcentage (%)',
  FIXED_AMOUNT: 'Montant fixe',
};

// --- LOT 6B : French labels -----------------------------------------------------

export const CALC_METHOD_LABELS: Record<CalcMethodCode, string> = {
  COUT: 'Coût (reconstruction)',
  COMPARAISON: 'Comparaison',
  CAPITALISATION: 'Capitalisation',
  MOYENNE: 'Moyenne',
};

export const CALC_STATUS_LABELS: Record<CalcRunStatus, string> = {
  BROUILLON: 'Brouillon',
  PREPARE: 'Préparé',
  BLOQUE: 'Bloqué',
  CALCULE: 'Calculé',
  EN_REVUE: 'En revue',
  VALIDE: 'Validé',
  FINALISE: 'Finalisé',
  REMPLACE: 'Remplacé',
  ANNULE: 'Annulé',
};

// --- LOT 7A : French labels -----------------------------------------------------

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  PV_BATI: 'PV de constat (bâti)',
  PV_EXPERTISE_COMPLETE: 'PV d’expertise complète',
  RAPPORT_EXPERTISE: 'Rapport d’expertise',
  DOCUMENT_METHODE_SPECIFIQUE: 'Document méthode spécifique',
};

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  BROUILLON: 'Brouillon',
  DOCX_GENERATED: 'DOCX généré',
  PDFA_VALIDATION_PENDING: 'PDF/A en attente de validation',
  PDFA_VALID: 'PDF/A validé',
  PDFA_VALIDATION_FAILED: 'PDF/A invalide',
  FINALISE: 'Finalisé',
  ARCHIVE: 'Archivé',
  REMPLACE: 'Remplacé',
  ANNULE: 'Annulé',
};

export const PDFA_STATUS_LABELS: Record<PdfaStatus, string> = {
  NOT_GENERATED: 'Non généré (LOT 7B)',
  GENERATED: 'Généré',
  VALIDATION_FAILED: 'Validation échouée',
  VALID: 'Validé',
  ARCHIVED: 'Archivé',
};

export const TEMPLATE_VERSION_STATUS_LABELS: Record<TemplateVersionStatus, string> = {
  BROUILLON: 'Brouillon',
  ACTIF: 'Actif',
  ARCHIVE: 'Archivé',
};

// --- LOT 7B : French labels -----------------------------------------------------

export const PIPELINE_STATUS_LABELS: Record<PipelineStatus, string> = {
  PENDING: 'En attente',
  PDF_CONVERSION_STARTED: 'Conversion PDF démarrée',
  PDF_GENERATED: 'PDF généré',
  PDFA_CONVERSION_STARTED: 'Conversion PDF/A démarrée',
  PDFA_GENERATED: 'PDF/A généré',
  PDFA_VALIDATION_STARTED: 'Validation PDF/A démarrée',
  PDFA_VALID: 'PDF/A valide',
  PDFA_VALIDATION_FAILED: 'Validation PDF/A échouée',
  GHOSTSCRIPT_REMEDIATION_STARTED: 'Remédiation Ghostscript démarrée',
  REMEDIATED_PDFA_GENERATED: 'PDF/A remédié généré',
  PDFA_REVALIDATION_STARTED: 'Revalidation PDF/A démarrée',
  PDFA_VALIDATION_FAILED_FINAL: 'Validation PDF/A échouée (définitif)',
  FAILED: 'Échec',
};
